import "pixi";
import "phaser";

import * as collision from "./collision";
import * as platform from "./platform";
import * as player from "./player";
import { BlurX, BlurY } from "./effects/blur";

let hitPointTexture: Phaser.BitmapData | null = null;

export class Laser {
    game: Phaser.Game;
    group: Phaser.Group;

    emitter: Phaser.Sprite;
    beam: Phaser.Graphics;
    direction: collision.Slope;
    hitPoint: Phaser.Sprite;
    extent: number;

    name: string;

    constructor(game: Phaser.Game, json: any, levelScale: number = 1) {
        let x = json.x * levelScale, y = json.y * levelScale;

        this.game = game;
        const mag = Math.sqrt(json.dx*json.dx + json.dy*json.dy);
        this.direction = { dx: json.dx / mag, dy: json.dy / mag };
        this.name = json.message;
        this.extent = 1600;

        this.group = game.add.group();
        this.group.position.set(x, y);

        this.beam = game.add.graphics(0, 0);
        this.beam.blendMode = PIXI.blendModes.ADD;
        this.beam.filters = [ new BlurX(game, 2048), new BlurY(game, 2048) ];
        this.emitter = game.add.sprite(16, 16, "laser");
        this.emitter.width = 32;
        this.emitter.height = (22 / 40) * 32;
        this.emitter.anchor.setTo(0.5, 0.5);
        this.emitter.pivot.set(0.5, 0.5);

        this.game.physics.p2.enable(this.emitter);
        this.emitter.body.setRectangle(32, 32, 8, 0);
        this.emitter.body.static = true;
        this.emitter.body.rotation = Math.atan2(this.direction.dy, this.direction.dx) - Math.PI / 2;

        this.group.add(this.beam);
        this.group.add(this.emitter);

        if (!hitPointTexture) {
            const bmd = game.make.bitmapData(32, 32);
            bmd.cls();
            const gradient = bmd.context.createRadialGradient(16, 16, 0, 16, 16, 10);
            gradient.addColorStop(0, "#FFFFFF");
            gradient.addColorStop(1, "rgba(255, 0, 0, 0)");
            bmd.circle(16, 16, 7, gradient as any);
            hitPointTexture = bmd;
        }

        this.hitPoint = game.add.sprite(0, 0, hitPointTexture, 0, this.group);
        this.hitPoint.anchor.setTo(0.5, 0.5);
        this.hitPoint.blendMode = PIXI.blendModes.ADD;
        this.hitPoint.alpha = 0.9;

        this.game.add.tween(this.hitPoint).to({
            alpha: 1.0,
        }, 100).loop().yoyo(true).start();

        this.redraw();
    }

    redraw(supercharge=false) {
        const endX = 16 + this.extent * this.direction.dx;
        const endY = 16 + this.extent * this.direction.dy;
        this.beam.clear();
        this.hitPoint.position.setTo(endX, endY);

        this.beam.lineStyle(supercharge ? 7 : 4.5, 0xFF0000);
        this.beam.moveTo(16, 16);
        this.beam.lineTo(16 + this.extent * this.direction.dx, 16 + this.extent * this.direction.dy);
        this.beam.lineStyle(supercharge ? 3 : 0.75, 0xFFFFFF);
        this.beam.moveTo(16, 16);
        this.beam.lineTo(endX, endY);
    }

    get displayObject(): PIXI.DisplayObject {
        return this.group;
    }

    get aabb(): collision.AABBLike {
        return {
            x: this.group.x,
            y: this.group.y,
            width: 32,
            height: 32,
        }
    }

    translate(dx: number, dy: number): void {
        this.group.x += dx;
        this.group.y += dy;
    }

    constrainTranslation(
        offsetX: number, offsetY: number,
        dx: number, dy: number,
        bbox: collision.AABBLike,
    ): [number, collision.ConstraintDirection[]] {
        let aabb = this.aabb;
        aabb.x += offsetX;
        aabb.y += offsetY;
        return collision.constrainAABBTranslation(
            aabb,
            dx, dy,
            bbox
        );
    }

    intersects(line: collision.Line): boolean {
        return line.intersectsAABB(this.aabb);
    }

    whichSide(line: collision.Line): collision.LineSide {
        return line.whichSide(this.aabb);
    }

    highlightIntersection(highlight: boolean): void {
        this.emitter.tint = highlight ? 0xFF0000 : 0xFFFFFF;
    }

    intersectsPlayer(offset: collision.PointLike, aabb: collision.AABBLike): boolean {
        const time = this.laserLine(offset).intersectsAABBTime(aabb);
        if (time === null) return false;
        this.redraw(time <= this.extent);
        return time <= this.extent;
    }

    laserLine(offset: collision.PointLike): collision.Ray {
        return new collision.Ray({
            x: this.group.x + offset.x + 16, y: this.group.y + offset.y + 16,
        }, this.direction);
    }

    updateExtent(t: number) {
        this.extent = t;
    }

    serialize(): any {
        return {
            type: "laser",
            x: this.group.position.x,
            y: this.group.position.y,
            dx: this.direction.dx,
            dy: this.direction.dy
        };
    }

    serializeMutable(): any {
        return {
            x: this.group.position.x,
            y: this.group.position.y,
        };
    }

    deserializeMutable(json: any, levelScale: number = 1): void {
        this.group.x = json.x * levelScale;
        this.group.y = json.y * levelScale;
    }
}

export class LaserCollection {
    lasers: Laser[];
    platforms: platform.Platform[];

    laserOffsets: collision.PointLike[];
    platformOffsets: collision.PointLike[];
    playerOffset: collision.PointLike;

    constructor(platforms: platform.Platform[], lasers: Laser[]) {
        this.lasers = lasers;
        this.platforms = platforms;
        this.resetOffsets();
    }

    resetOffsets() {
        this.laserOffsets = [];
        this.platformOffsets = [];
        this.lasers.forEach(() => this.laserOffsets.push({ x: 0, y: 0 }));
        this.platforms.forEach(() => this.platformOffsets.push({ x: 0, y: 0 }));
        this.playerOffset = { x: 0, y: 0 };
    }

    update(player: player.Player, offsetFor: (obj: any) => collision.PointLike): Laser | null {
        let hitLaser = null;
        const playerOffset = offsetFor(player);

        for (let laserIdx = 0; laserIdx < this.lasers.length; laserIdx++) {
            const laser = this.lasers[laserIdx];
            const offset = offsetFor(laser);
            let extent = 1600;

            for (const platform of this.platforms) {
                if (platform.intersectionTime) {
                    const offset2 = offsetFor(platform);
                    const totalOffset = { x: offset.x - offset2.x,
                                          y: offset.y - offset2.y };
                    const t = platform.intersectionTime(laser.laserLine(totalOffset));
                    if (t) extent = Math.min(extent, t);
                }
            }
            laser.updateExtent(extent);
            laser.redraw();

            let playerAABB = player.aabb;
            playerAABB.x += playerOffset.x;
            playerAABB.y += playerOffset.y;
            if (laser.intersectsPlayer(offset, playerAABB)) {
                hitLaser = laser;
            }

            // Optimization: if the player ended under a laser, then
            // don't bother doing the more expensive check below.
            if (hitLaser) continue;
            // Compute relative motion of laser and player, and
            // perform intersection test with resulting motion vector
            // and infinite-length beam.
            const curPos = new Phaser.Point(
                playerAABB.x - offset.x,
                playerAABB.y - offset.y);
            const baseAABB = player.aabb;
            const prevPos = new Phaser.Point(
                baseAABB.x + this.playerOffset.x - this.laserOffsets[laserIdx].x,
                baseAABB.y + this.playerOffset.y - this.laserOffsets[laserIdx].y);
            const relMotion = curPos.clone().subtract(prevPos.x, prevPos.y);

            if (Math.abs(relMotion.x) < 0.1 && Math.abs(relMotion.y) < 0.1) {
                // If there was no noticeable relative motion, skip to
                // avoid the more expensive check.
                // TODO: we can probably provide a tighter bound on
                // how much relative motion is too much.
                continue;
            }
            // If the beam intersects the player's motion vector, the
            // player potentially swept through a beam. In this case,
            // check collision with all platforms to make sure the
            // player wasn't saved by a platform.

            // For now I'm just going to inline the motion
            // vector-laser ray intersection test.
            // https://rootllama.wordpress.com/2014/06/20/ray-line-segment-intersection-test-in-2d/
            const O = new Phaser.Point(laser.aabb.x, laser.aabb.y);
            const v1 = O.clone().subtract(prevPos.x, prevPos.y);
            const v2 = relMotion;
            const v3 = new Phaser.Point(-laser.direction.dy, laser.direction.dx);
            const t1 = v2.cross(v1) / v2.dot(v3);
            const t2 = v1.dot(v3) / v2.dot(v3);

            if (t1 < 0 || t2 < 0 || t2 > 1) {
                // Either the intersection happened behind the laser,
                // or it happened outside the path of motion of the
                // player.
                continue;
            }

            // The laser swept across the player at some point. t2 is
            // the time at which the player translation caused the
            // player to intersect the laser. t1 is the time at which
            // the laser beam hits the player, from the laser beam's
            // perspective. Now go through every object, and check
            // whether any intersected the player earlier than t1.
            console.log("Player potentially collided with laser at time", t2);
            let safe = false;
            // Now we go through and check if any platform protected
            // the player.

            // Change in laser offset since last frame
            const laserOffsetDx = offset.x - this.laserOffsets[laserIdx].x;
            const laserOffsetDy = offset.y - this.laserOffsets[laserIdx].y;
            for (let platformIdx = 0; platformIdx < this.platforms.length; platformIdx++) {
                const platform = this.platforms[platformIdx];
                if (platform.intersectionTime) {
                    const platformOffset = offsetFor(platform);
                    const prevOffset = this.platformOffsets[platformIdx];
                    // Calculate time of intersection as if everything
                    // had stood still except for this laser. The
                    // laser's relative position is then the previous
                    // offset plus the % of the change in offset that
                    // had occurred when the beam swept past the
                    // player, minus the same calculation for the
                    // platform.
                    const tMiddle = platform.intersectionTime(laser.laserLine({
                        x: this.laserOffsets[laserIdx].x + t2 * laserOffsetDx -
                            (prevOffset.x + t2 * (platformOffset.x - prevOffset.x)),
                        y: this.laserOffsets[laserIdx].y + t2 * laserOffsetDy -
                            (prevOffset.y + t2 * (platformOffset.y - prevOffset.y)),
                    }));
                    if (tMiddle && tMiddle < t1) {
                        // The laser hit something before it hit the
                        // player, so the player is safe.
                        console.log("Protected by platform");
                        safe = true;
                        break;
                    }
                }
            }
            if (!safe) {
                hitLaser = laser;
                continue;
            }
        }

        this.playerOffset = playerOffset;
        for (let laserIdx = 0; laserIdx < this.lasers.length; laserIdx++) {
            this.laserOffsets[laserIdx] = offsetFor(this.lasers[laserIdx]);
        }
        for (let platformIdx = 0; platformIdx < this.platforms.length; platformIdx++) {
            this.platformOffsets[platformIdx] = offsetFor(this.platforms[platformIdx]);
        }

        return hitLaser;
    }
}



// WEBPACK FOOTER //
// ./src/laser.ts